import {
  Button,
  UpdateIconButton,
  formatDate,
  formatSchoolYear,
  ListContainer,
  ListContext,
  NoData,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  pn,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Typography,
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  Permission,
  PermissionAction,
  ProviderEmploymentListDto,
  ProviderEmploymentListFilter,
  ProviderEmploymentListResource,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import { providerEmploymentPageRoute } from "pages/providers/provider-employments/ProviderEmploymentPage/ProviderEmploymentPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const Employments = (props: { providerId: number }) => {
  const ROOT_PERMISSION: Permission = Permission.ProviderEmployment;
  const navigate = useNavigate();

  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<ProviderEmploymentListFilter>({
    employmentFilter: { providerId: props.providerId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<ProviderEmploymentListDto>("startDate"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<ProviderEmploymentListDto[]>([]);
  const [resource, setResource] = useState<ProviderEmploymentListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.providerEmployments.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.providerEmployments.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  return (
    <>
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="inherit">Employments</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  variant="text"
                  onClick={() =>
                    navigate({
                      pathname: providerEmploymentPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                      search: providerEmploymentPageRoute.getOptionalParams?.({
                        providerId: props.providerId,
                      }),
                    })
                  }
                  sx={{ minWidth: "auto" }}
                >
                  + Employment
                </Button>
              )}
            </Stack>
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ProviderEmploymentListDto>("startDate")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<ProviderEmploymentListDto>("endDate"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<ProviderEmploymentListDto>("school"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ProviderEmploymentListDto>("endReason")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: providerEmploymentPageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                      })
                    }
                  >
                    <TableCell>{formatDate(listItem.startDate)}</TableCell>
                    <TableCell>{formatDate(listItem.endDate)}</TableCell>
                    <TableCell>{listItem.school}</TableCell>
                    <TableCell>{listItem.endReason}</TableCell>
                    <TableCell type="action">
                      {isAuthorized(ROOT_PERMISSION, PermissionAction.Update) && (
                        <UpdateIconButton color="primary"
                          onClick={() =>
                            navigate({
                              pathname: providerEmploymentPageRoute.getPath({
                                id: listItem.id,
                                crudMode: "edit",
                                screenType: "dialog",
                              }),
                            })
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        {matchTemplatePath(providerEmploymentPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
